import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { PbjBreakRule, PbjBreakRulesSaveBody, PbjBreakRulesSaveResult } from "../../../utils/models";
import { pbjCachedQueryOptions } from "./pbjQueryOptions";

export const PBJ_BREAK_RULES_QUERY_KEY = "pbj-break-rules";

/** The department's PBJ break rules (the Breaks-tab grid source). */
export const usePbjBreakRules = (departmentId: string) =>
	useQuery<PbjBreakRule[]>({
		queryKey: [PBJ_BREAK_RULES_QUERY_KEY, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjBreakRules(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
		// Dept config, only mutated via useSavePbjBreakRules (setQueryData/invalidate) — never auto-refetch.
		...pbjCachedQueryOptions,
	});

/**
 * Replace the dept's pbj break rules. On success refreshes the current dept's cache from the result;
 * when applied to the network, invalidates every PBJ break-rules cache since sibling depts may be cached.
 */
export const useSavePbjBreakRules = (departmentId: string) => {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<PbjBreakRulesSaveResult, Error, PbjBreakRulesSaveBody>({
		mutationFn: async (body) => {
			const res = await backendService.savePbjBreakRules(body);
			return res.data;
		},
		onSuccess: (result, body) => {
			queryClient.setQueryData<PbjBreakRule[]>([PBJ_BREAK_RULES_QUERY_KEY, departmentId], result.rules);
			if (body.applyToNetwork) {
				queryClient.invalidateQueries({ queryKey: [PBJ_BREAK_RULES_QUERY_KEY] });
			}
			showSnackbar(t("pbj_model.breaks.saved", { count: result.affectedDepartmentIds.length }), "success");
			track("pbj_break_rules_saved", {
				department_id: departmentId,
				apply_to_network: body.applyToNetwork,
				n_rules: body.rules.length,
				affected_count: result.affectedDepartmentIds.length,
			});
		},
		onError: (error) => {
			showSnackbar(t("pbj_model.breaks.save_failed"), "error");
			trackError("pbj_break_rules_save_error", { error_message: error?.message });
		},
	});
};
